import {Box, Typography} from '@mui/material'
import React from 'react'

import {useRadioButtonStyles} from './RadioButton.styles'
import {RadioButtonProps} from './RadioButton.types'

/**
 * RadioButton - Individual radio button component
 *
 * A single radio button option with visual variants (neutral/warning).
 * Displays a circular radio indicator followed by text label.
 *
 * @example
 * // Standalone neutral variant
 * <RadioButton
 *   selected={isSelected}
 *   onPress={() => setSelected(true)}
 *   label="I agree"
 *   variant="neutral"
 * />
 *
 * @example
 * // Warning variant (orange)
 * <RadioButton
 *   selected={isRejected}
 *   onPress={() => setRejected(true)}
 *   label="No, cancel order"
 *   variant="warning"
 * />
 *
 * @example
 * // Disabled state
 * <RadioButton
 *   selected={false}
 *   onPress={() => {}}
 *   label="Not available"
 *   disabled={true}
 * />
 */
export const RadioButton: React.FC<RadioButtonProps> = ({
  selected,
  onPress,
  label,
  disabled = false,
  variant = 'neutral',
  testId,
  className
}) => {
  const {classes} = useRadioButtonStyles({selected, disabled, variant})

  const handleClick = () => {
    if (!disabled) {
      onPress()
    }
  }

  return (
    <Box
      onClick={handleClick}
      data-test-id={testId}
      className={`${classes.container} ${className || ''}`}
      role="radio"
      aria-checked={selected}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      onKeyDown={(e) => {
        if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault()
          onPress()
        }
      }}
    >
      <Box className={classes.indicator}>{selected && <Box className={classes.innerDot} />}</Box>
      <Typography className={classes.label}>{label}</Typography>
    </Box>
  )
}
